import { useFilter } from "hooks/filter";
import { Icon } from "components/ui";
import { IconName } from "@fortawesome/fontawesome-common-types";
import { ContentCategory } from "types/appConfigV2";
import { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { HeaderListItem } from "./HeaderListItem";

export default function Header(props: {
  categories: { [key: string]: ContentCategory };
}) {
  const { searchDispatch, searchState } = useFilter();
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.sm - 100}px)`);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [shouldShowHamburger, setShouldShowHamburger] = useState(isMobile || (isSmallScreen && Object.keys(props.categories).length > 7));
  const [selectedCategoryTitle, setSelectedCategoryTitle] = useState(searchState.currentCategory.title);
  const open = Boolean(anchorEl);
  const selectedTitleRef = useRef("");
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const check = () => {
      const el = textRef.current;
      if (el) {
        setShowTooltip(el.scrollWidth > el.clientWidth);
      }
    };

    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [searchState.currentCategory.title]);



  useEffect(() => {
    selectedTitleRef.current = selectedCategoryTitle;
  }, [selectedCategoryTitle]);


  useEffect(() => {
    const handleResize = () => {
      setMenuType(selectedTitleRef.current);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const getTextWidth = (text: string, font: string = "Open Sans") => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      console.error("2D context not available, returning 0 width");
      return 0;
    }

    // set font: weight + size + family
    //size and weight hard coded on css
    context.font = `400 18px ${font}`;
    const metrics = context.measureText(text);
    return metrics.width;
  }

  const setMenuType = (title: string) => {
    const textWidth = getTextWidth(title, "Open Sans");
    const iconWidth = 40;
    const margin = 40;
    const middleGap = 16;
    const totalIconWidth = (iconWidth * Object.keys(props.categories).length) + margin;
    const isHamugerMenu = (textWidth + totalIconWidth + middleGap) > window.innerWidth;
    setShouldShowHamburger(isHamugerMenu);
  }

  const handleCategorySelect = (category: ContentCategory) => {
    searchDispatch({
      type: "CHANGE_CATEGORY",
      payload: category,
    });
    setSelectedCategoryTitle(category.title);
    setMenuType(category.title);
    handleMenuClose();
  };

  return (
    <Box mb={2} sx={{ flexGrow: 1 }}>
      <Grid
        alignItems="center"
        justifyContent="space-between"
        container
        spacing={2}
      >
        <Grid item xs sx={{ minWidth: 0 }}>
          <Tooltip
            title={searchState.currentCategory.title}
            disableHoverListener={!showTooltip}
            sx={{ display: "block", width: "100%", minWidth: 0, overflow: "hidden" }}
          >
            <Typography
              ref={textRef}
              variant="h6"
              noWrap
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {searchState.currentCategory.title}
            </Typography>
          </Tooltip>
        </Grid>
        <Grid item>
          {shouldShowHamburger ? (
            // Mobile: Hamburger menu
            <>
              <Tooltip title="Targets">
                <IconButton
                  onClick={handleMenuClick}
                  size="small"
                  sx={{
                    color: 'primary.main',
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-menu-icon lucide-menu"><path d="M4 5h16" /><path d="M4 12h16" /><path d="M4 19h16" /></svg>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    maxHeight: 'none',
                    minWidth: '200px',
                    overflow: 'visible',
                  },
                }}
              >
                {Object.entries(props.categories).map((entry) => (
                  <MenuItem
                    key={entry[1].id}
                    onClick={() => handleCategorySelect(entry[1])}
                    selected={searchState.currentCategory.id === entry[1].id}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(0, 158, 224, 0.12)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 158, 224, 0.12)',
                        },
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Icon name={entry[1].icon as IconName} />
                    </ListItemIcon>
                    <HeaderListItem text={entry[1].title} />
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
          <ToggleButtonGroup
            color="primary"
            exclusive={true}
            size="small"
            onChange={(event: React.MouseEvent<HTMLElement>, category: any) => {
              if (category !== null) {
                searchDispatch({
                  type: "CHANGE_CATEGORY",
                  payload: category,
                });
                setSelectedCategoryTitle(category.title);
                setMenuType(category.title);
              }
            }}
          >
            {Object.entries(props.categories).map((entry) => (
              <Tooltip title={entry[1].title} key={entry[1].id}>
                <ToggleButton
                  key={entry[1].id}
                  disableRipple
                  value={entry[1]}
                  selected={searchState.currentCategory.id === entry[1].id}
                >
                  <Icon name={entry[1].icon as IconName} />
                </ToggleButton>
              </Tooltip>
            ))}
          </ToggleButtonGroup>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
